import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { 
  BookOpen, 
  Users, 
  TrendingUp, 
  Award, 
  Clock,
  Plus,
  Eye,
  Edit,
  BarChart3,
  Star,
  Calendar,
  Target
} from 'lucide-react';
import { motion } from 'framer-motion';
import useUserStore from '../../store/userStore';
import api, { endpoints } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import StatsCard from '../../components/Cards/StatsCard';
import CourseCard from '../../components/Cards/CourseCard';
import ProgressChart from '../../components/Charts/ProgressChart';
import SkillsChart from '../../components/Charts/SkillsChart';
import toast from 'react-hot-toast';

const TrainerDashboard = () => {
  const { user } = useUserStore();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch trainer dashboard data
  const { data: dashboardData, isLoading, error } = useQuery(
    'trainer-dashboard',
    async () => {
      const response = await api.get(endpoints.analytics.trainer);
      return response.data;
    },
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      onError: (error) => {
        toast.error('Failed to load dashboard data');
      }
    }
  );

  // Mock data for demonstration
  const mockStats = {
    totalCourses: 8,
    activeStudents: 156,
    totalEnrollments: 324,
    averageRating: 4.7,
    totalRevenue: 125000,
    completionRate: 78
  };

  const mockCourses = [
    {
      id: 1,
      title: 'Advanced Python Programming',
      description: 'Master advanced Python concepts and frameworks',
      status: 'published',
      enrollment_count: 45,
      average_rating: 4.8,
      completion_rate: 82,
      revenue: 45000,
      created_at: '2024-01-15'
    },
    {
      id: 2,
      title: 'React Development Bootcamp',
      description: 'Complete React development course',
      status: 'published',
      enrollment_count: 38,
      average_rating: 4.6,
      completion_rate: 75,
      revenue: 32000,
      created_at: '2024-02-01'
    },
    {
      id: 3,
      title: 'Machine Learning Fundamentals',
      description: 'Introduction to ML concepts and algorithms',
      status: 'draft',
      enrollment_count: 0,
      average_rating: 0,
      completion_rate: 0,
      revenue: 0,
      created_at: '2024-02-15'
    }
  ];

  const mockProgress = [
    { month: 'Jan', enrollments: 25, completions: 18 },
    { month: 'Feb', enrollments: 35, completions: 28 },
    { month: 'Mar', enrollments: 42, completions: 32 },
    { month: 'Apr', enrollments: 38, completions: 30 },
    { month: 'May', enrollments: 45, completions: 35 },
    { month: 'Jun', enrollments: 52, completions: 41 },
  ];

  const mockSkills = [
    { name: 'Python', students: 85, trend: 'up' },
    { name: 'React', students: 70, trend: 'up' },
    { name: 'JavaScript', students: 90, trend: 'stable' },
    { name: 'Node.js', students: 60, trend: 'up' },
    { name: 'SQL', students: 75, trend: 'stable' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg">Failed to load dashboard</div>
        <p className="text-gray-600 mt-2">Please try refreshing the page</p>
      </div>
    );
  }

  const stats = dashboardData?.stats || mockStats;
  const courses = dashboardData?.courses || mockCourses;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-secondary-600 to-primary-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Welcome back, {user?.first_name}!</h1>
            <p className="text-secondary-100 mt-2">
              Manage your courses and track your teaching impact.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5" />
                <span className="text-sm font-medium">Top Trainer</span>
              </div>
              <p className="text-xs text-secondary-100 mt-1">4.7★ Average Rating</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Courses"
          value={stats.totalCourses}
          icon={BookOpen}
          color="primary"
          trend="+2 this month"
        />
        <StatsCard
          title="Active Students"
          value={stats.activeStudents}
          icon={Users}
          color="secondary"
          trend="+12 this week"
        />
        <StatsCard
          title="Total Enrollments"
          value={stats.totalEnrollments}
          icon={TrendingUp}
          color="accent"
          trend="+8% this month"
        />
        <StatsCard
          title="Average Rating"
          value={stats.averageRating}
          icon={Star}
          color="primary"
          trend="+0.2 this month"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'courses', name: 'My Courses', icon: BookOpen },
            { id: 'students', name: 'Students', icon: Users },
            { id: 'analytics', name: 'Analytics', icon: TrendingUp },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Enrollment Trends</h3>
                  <p className="card-description">Course enrollments and completions over time</p>
                </div>
                <div className="card-content">
                  <ProgressChart data={mockProgress} />
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Popular Skills</h3>
                  <p className="card-description">Skills your students are learning</p>
                </div>
                <div className="card-content">
                  <SkillsChart data={mockSkills} />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Quick Actions</h3>
                <p className="card-description">Manage your teaching activities</p>
              </div>
              <div className="card-content">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <Plus className="h-8 w-8 text-primary-600 mb-2" />
                    <h4 className="font-medium text-gray-900">Create New Course</h4>
                    <p className="text-sm text-gray-600">Start building your next course</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="p-4 border border-gray-200 rounded-lg hover:border-secondary-300 hover:bg-secondary-50 transition-colors"
                  >
                    <Users className="h-8 w-8 text-secondary-600 mb-2" />
                    <h4 className="font-medium text-gray-900">View Students</h4>
                    <p className="text-sm text-gray-600">Manage your student base</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="p-4 border border-gray-200 rounded-lg hover:border-accent-300 hover:bg-accent-50 transition-colors"
                  >
                    <BarChart3 className="h-8 w-8 text-accent-600 mb-2" />
                    <h4 className="font-medium text-gray-900">View Analytics</h4>
                    <p className="text-sm text-gray-600">Track your performance</p>
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">My Courses</h3>
                <p className="text-gray-600">Manage and track your course performance</p>
              </div>
              <button className="btn btn-primary btn-sm">
                <Plus className="h-4 w-4 mr-2" />
                Create Course
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card hover-lift"
                >
                  <div className="card-content">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{course.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{course.description}</p>
                      </div>
                      <span className={`badge ${
                        course.status === 'published' ? 'badge-success' : 'badge-warning'
                      }`}>
                        {course.status}
                      </span>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Users className="h-4 w-4" />
                          <span>{course.enrollment_count} students</span>
                        </div>
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Star className="h-4 w-4 text-yellow-500" />
                          <span>{course.average_rating.toFixed(1)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Target className="h-4 w-4" />
                          <span>{course.completion_rate}% completion</span>
                        </div>
                        <div className="flex items-center space-x-1 text-gray-600">
                          <TrendingUp className="h-4 w-4" />
                          <span>₹{course.revenue.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <button className="btn btn-ghost btn-sm">
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </button>
                      <button className="btn btn-ghost btn-sm">
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'students' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Student Management</h3>
                <p className="text-gray-600">Track and manage your students' progress</p>
              </div>
            </div>

            <div className="card">
              <div className="card-content">
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Student Management</h3>
                  <p className="text-gray-600">Student management features coming soon</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
                <p className="text-gray-600">Detailed analytics and insights</p>
              </div>
            </div>

            <div className="card">
              <div className="card-content">
                <div className="text-center py-12">
                  <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Analytics Dashboard</h3>
                  <p className="text-gray-600">Advanced analytics features coming soon</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainerDashboard;
